function isActiveContact(contact) {
  return contact?.frequency && contact.frequency !== 'None';
}

function isPendingMessage(message) {
  return message?.messageStatus === 'Pending' || message?.status === 'Pending';
}

export function getActiveContacts(contacts = []) {
  return contacts.filter(isActiveContact);
}

export function collectPendingTouches(contacts = []) {
  const touches = [];

  getActiveContacts(contacts).forEach((contact) => {
    if (!Array.isArray(contact.messageQueue)) {
      return;
    }

    contact.messageQueue.forEach((message, index) => {
      if (!isPendingMessage(message)) {
        return;
      }

      touches.push({
        id: message.id || `${contact.uid || contact.id}-${index}`,
        contactId: contact.uid || contact.id,
        contact,
        message,
        subject: message.title || message.subject || 'No subject',
        preview:
          message.firstParagraph ||
          message.secondParagraph ||
          message.preview ||
          '',
        days: contact.sendDate ? `${contact.sendDate} Days` : '—',
        sendDate: contact.sendDate,
        status: message.messageStatus || message.status || 'Pending',
      });
    });
  });

  touches.sort((a, b) => {
    const aDate = Number(a.sendDate) || 999;
    const bDate = Number(b.sendDate) || 999;
    return aDate - bDate;
  });

  return touches;
}

export function collectTouchesForContact(contact) {
  if (!contact || !Array.isArray(contact.messageQueue)) {
    return [];
  }

  const contactId = contact.uid || contact.id;

  return contact.messageQueue.map((message, index) => {
    const status = message.messageStatus || message.status || 'Unknown';
    return {
      id: message.id || `${contactId}-${index}`,
      contactId,
      contact,
      message,
      subject: message.title || message.subject || 'No subject',
      preview:
        message.firstParagraph ||
        message.secondParagraph ||
        message.preview ||
        '',
      days: contact.sendDate ? `${contact.sendDate} Days` : '—',
      sendDate: contact.sendDate,
      status,
    };
  }).reverse();
}

export function collectSentThisMonth(contacts = []) {
  const now = new Date();
  const month = now.getMonth();
  const year = now.getFullYear();
  let count = 0;

  contacts.forEach((contact) => {
    if (!Array.isArray(contact.messageQueue)) {
      return;
    }

    contact.messageQueue.forEach((message) => {
      if (message.messageStatus !== 'Sent' || !message.sentAt) {
        return;
      }

      const sent = new Date(message.sentAt);
      if (sent.getMonth() === month && sent.getFullYear() === year) {
        count += 1;
      }
    });
  });

  return count;
}

export function computePipelineWarmth(contacts = []) {
  const active = getActiveContacts(contacts);
  const total = active.length;

  if (total === 0) {
    return { total: 0, touched: 0, percent: 0 };
  }

  const cycleMs = 30 * 24 * 60 * 60 * 1000;
  const cutoff = Date.now() - cycleMs;

  const touched = active.filter((contact) => {
    if (!Array.isArray(contact.messageQueue)) {
      return false;
    }

    return contact.messageQueue.some((message) => {
      if (message.messageStatus !== 'Sent' || !message.sentAt) {
        return false;
      }

      return new Date(message.sentAt).getTime() >= cutoff;
    });
  }).length;

  return {
    total,
    touched,
    percent: Math.round((touched / total) * 100),
  };
}

export function getRecentContacts(contacts = [], limit = 5) {
  return [...getActiveContacts(contacts)]
    .sort((a, b) => {
      const aTime = a.createdAt || 0;
      const bTime = b.createdAt || 0;
      return bTime - aTime;
    })
    .slice(0, limit);
}

export function formatAddedLabel(createdAt) {
  if (!createdAt) {
    return '';
  }

  const date = typeof createdAt === 'number' ? new Date(createdAt) : new Date(createdAt);
  const diffMs = Date.now() - date.getTime();
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (days <= 0) {
    return 'Today';
  }
  if (days === 1) {
    return '1 day ago';
  }
  if (days < 7) {
    return `${days} days ago`;
  }
  if (days < 14) {
    return '1 week ago';
  }
  if (days < 30) {
    return `${Math.floor(days / 7)} weeks ago`;
  }

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function contactsAddedThisWeek(contacts = []) {
  const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  return getActiveContacts(contacts).filter((contact) => {
    const created = contact.createdAt || 0;
    return created >= weekAgo;
  }).length;
}

export function messageBodyParagraphs(message = {}) {
  return [message.firstParagraph, message.secondParagraph, message.thirdParagraph].filter(Boolean);
}

export function messageArticles(message = {}) {
  if (!Array.isArray(message.bulletPoints)) {
    return [];
  }

  return message.bulletPoints.map((item, index) => ({
    id: index,
    title: item.bulletPointBold || item.title || item.bulletPoint || 'Article',
    source: item.bulletPointSource || item.source || item.url || 'Source',
    url: item.bulletPointUrl || item.url,
    selected: index < 2,
  }));
}

export function buildInitialArticleSelection(articles = [], maxSelected = 2) {
  const selection = {};
  articles.forEach((_, index) => {
    if (index < maxSelected) {
      selection[index] = true;
    }
  });
  return selection;
}

export function contactDisplayName(contact = {}) {
  return contact.name || 'Unknown';
}

export function contactSubtitle(contact = {}) {
  const title = contact.jobTitle || contact.role || contact.title || '';
  const company = contact.companyName || contact.company || '';
  if (title && company) {
    return `${title} · ${company}`;
  }
  return title || company || '';
}

export function normalizeTriggers(contact = {}) {
  const raw = contact.triggers || contact.interests;
  if (Array.isArray(raw)) {
    return raw.filter(Boolean);
  }
  if (typeof raw === 'string' && raw.trim()) {
    return raw.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return [];
}

export function contactHasPendingTouch(contact = {}) {
  if (!Array.isArray(contact.messageQueue)) {
    return false;
  }
  return contact.messageQueue.some(isPendingMessage);
}

export function mapContactForList(contact = {}) {
  const pending = contactHasPendingTouch(contact);
  return {
    ...contact,
    triggers: normalizeTriggers(contact),
    status: pending ? 'pending' : 'scheduled',
    followUp:
      contact.followUpDate ||
      contact.nextFollowUp ||
      (contact.sendDate ? `in ${contact.sendDate} days` : '—'),
  };
}

export function getContactInteractions(contact = {}) {
  if (!Array.isArray(contact.messageQueue)) {
    return [];
  }

  return [...contact.messageQueue]
    .reverse()
    .slice(0, 8)
    .map((message) => ({
      subj: message.title || message.subject || 'Touch',
      status: message.messageStatus === 'Sent' ? 'sent' : 'pending',
      when: message.sentAt
        ? formatAddedLabel(new Date(message.sentAt).getTime())
        : contact.sendDate
          ? `in ${contact.sendDate} days`
          : '—',
    }));
}

export function countContactsWithPending(contacts = []) {
  return getActiveContacts(contacts).filter(contactHasPendingTouch).length;
}
